import React from "react";
import { RoughNotation } from "react-rough-notation";
import { MaskedWords, Reveal } from "../components/Reveal";
import { ThemedImage } from "../components/ThemedImage";
import { ABOUT_CHAPTERS, PROFILE, PHOTOS } from "../data/content";
import { Arrow, Squiggle, Star } from "../components/Scribbles";

export default function About() {
  return (
    <div className="px-5 md:px-10 pt-32 pb-28" data-testid="about-page">
      <header className="max-w-6xl mb-20 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <p className="font-handwritten text-3xl text-terracotta mb-4 rotate-[-2deg]">nice to meet you</p>
          <h1 className="font-serif font-light text-6xl md:text-8xl uppercase tracking-tighter text-ink leading-none">
            <MaskedWords text="About me" />
          </h1>
          <Squiggle width={180} className="mt-4" />
        </div>
        <div className="md:col-span-5 flex justify-end">
          <div className="relative rotate-[2deg]">
            <div className="border-2 border-ink p-3 bg-sand max-w-xs shadow-[8px_8px_0_0_rgba(26,26,26,0.15)]">
              <ThemedImage
                src={PHOTOS.portrait}
                alt="Arya"
                className="w-full h-80"
              />
              <p className="font-handwritten text-2xl text-ink mt-2 text-center">that&apos;s me →</p>
            </div>
            <Star size={30} className="absolute -top-4 -left-6" />
          </div>
        </div>
      </header>

      <div className="space-y-32 max-w-5xl">
        {ABOUT_CHAPTERS.map((ch) => (
          <Reveal key={ch.number}>
            <div className="relative" data-testid={`about-chapter-${ch.number}`}>
              <span className="absolute -top-16 md:-top-24 -left-2 font-serif text-[28vw] md:text-[15vw] leading-none text-ink/[0.06] -z-10 select-none">
                {ch.number}
              </span>
              <h2 className="font-serif italic text-3xl md:text-5xl text-terracotta">
                {ch.number}. {ch.title}
              </h2>
              <p className="mt-6 text-lg md:text-2xl text-ink leading-relaxed max-w-3xl font-light">
                {ch.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* MOMENTS — scrapbook strip */}
      <Reveal className="mt-32 border-t-2 border-ink pt-16 max-w-6xl">
        <div className="flex items-baseline justify-between mb-12">
          <div>
            <p className="font-handwritten text-3xl text-terracotta rotate-[-2deg]">off the clock</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ink">Moments</h2>
          </div>
          <span className="hidden md:block font-handwritten text-xl text-ink/50">a few polaroids</span>
        </div>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div className="relative rotate-[-3deg]" data-testid="moment-ladakh">
            <div className="border-2 border-ink p-3 bg-sand shadow-[10px_10px_0_0_rgba(26,26,26,0.12)]">
              <ThemedImage src={PHOTOS.ladakh} alt="Ladakh" className="w-full h-[420px]" intensity="soft" />
              <div className="flex items-center justify-between mt-3 px-2">
                <p className="font-handwritten text-2xl text-ink">Ladakh, on top of the world</p>
                <span className="font-serif italic text-sm text-ink/60">2024</span>
              </div>
            </div>
          </div>

          <div className="relative rotate-[3deg] md:mt-16" data-testid="moment-snow">
            <div className="border-2 border-ink p-3 bg-sand shadow-[10px_10px_0_0_rgba(26,26,26,0.12)]">
              <ThemedImage src={PHOTOS.snow} alt="Snow" className="w-full h-[420px]" intensity="soft" />
              <div className="flex items-center justify-between mt-3 px-2">
                <p className="font-handwritten text-2xl text-ink">ice-cream in the snow</p>
                <span className="font-serif italic text-sm text-ink/60">Rohtang</span>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal className="mt-32 border-t-2 border-ink pt-12 max-w-5xl">
        <p className="font-serif text-3xl md:text-5xl text-ink leading-tight">
          Currently based between{" "}
          <RoughNotation type="highlight" color="#D4A017" show animationDelay={300}>
            <span className="px-1">{PROFILE.location}</span>
          </RoughNotation>{" "}
          — always open to a good conversation.
        </p>
        <div className="flex items-center gap-4 mt-8">
          <a href="/handmade/contact" className="draw-link text-sm uppercase tracking-widest text-terracotta" data-testid="about-contact-cta">
            Let&apos;s talk
          </a>
          <Arrow size={54} />
        </div>
      </Reveal>
    </div>
  );
}
